import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const ActiveMatchContext = createContext(null);

const API        = process.env.REACT_APP_API_URL || '';
const LS_KEY     = 'sat_active_game';
const FORFEIT_S  = 30;   // must match server-side 30 000 ms timeout
const POLL_MS    = 4000; // how often to re-confirm with the server

export function ActiveMatchProvider({ children }) {
  const { user } = useAuth();

  // Hydrate synchronously from localStorage so the route guard can fire on the
  // very first render — no waiting for an API round-trip.
  const [activeMatch, setActiveMatchState] = useState(() => {
    try {
      const stored = localStorage.getItem(LS_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  // Write-through setter: keeps localStorage in sync for cross-tab awareness.
  const setMatch = useCallback((data) => {
    setActiveMatchState(data);
    try {
      if (data) localStorage.setItem(LS_KEY, JSON.stringify(data));
      else       localStorage.removeItem(LS_KEY);
    } catch {}
  }, []);

  const clearMatch = useCallback(() => setMatch(null), [setMatch]);

  // ── Server poll ─────────────────────────────────────────────────────────────
  // Confirms the active game is still live. Clears stale localStorage entries
  // (e.g. game ended while tab was closed, or server restarted).
  const pollRef = useRef(null);
  useEffect(() => {
    if (!user) { clearMatch(); return; }

    let cancelled = false;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/api/my-active-match`);
        if (!cancelled) setMatch(res.data || null);
      } catch { /* network blip — keep stale state rather than clearing */ }
    };

    poll();
    pollRef.current = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(pollRef.current); };
  }, [user, setMatch, clearMatch]);

  // ── Cross-tab sync ──────────────────────────────────────────────────────────
  // When another tab writes/clears sat_active_game, this tab updates instantly.
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== LS_KEY) return;
      try {
        setActiveMatchState(e.newValue ? JSON.parse(e.newValue) : null);
      } catch {}
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // ── Forfeit countdown ───────────────────────────────────────────────────────
  // Only active when the server has reported the player's socket as disconnected.
  const [secsUntilForfeit, setSecsUntilForfeit] = useState(null);
  useEffect(() => {
    if (!activeMatch?.disconnectedAt) { setSecsUntilForfeit(null); return; }
    const tick = () => {
      const elapsed  = (Date.now() - activeMatch.disconnectedAt) / 1000;
      const remaining = Math.ceil(Math.max(0, FORFEIT_S - elapsed));
      setSecsUntilForfeit(remaining);
    };
    tick();
    const iv = setInterval(tick, 500);
    return () => clearInterval(iv);
  }, [activeMatch?.disconnectedAt]);

  return (
    <ActiveMatchContext.Provider value={{ activeMatch, setMatch, clearMatch, secsUntilForfeit }}>
      {children}
    </ActiveMatchContext.Provider>
  );
}

export const useActiveMatch = () => useContext(ActiveMatchContext);
export default ActiveMatchContext;
